import React, {
  ComponentType,
  useEffect,
  useState
} from 'react'
import {
  Dropdown,
  Responsive
} from 'semantic-ui-react'
// Local
import { API_URL } from '../../constant'
import { api } from '../../action/api'
import { searchGenerator } from '../../action/search'

const SafeResponsive = Responsive as unknown as ComponentType<any>
const SafeDropdown = Dropdown as unknown as ComponentType<any>

type Practitioner = {
  id: string
  firstName: string
  lastName: string
}

type DropdownOption = {
  key: string
  value: string
  text: string
}

type PractitionerSelectProps = {
  onChange?: (
    event: React.SyntheticEvent,
    data: {
      name: string
      value: string | null
    }
  ) => void
  name?: string
  value?: string
  placeholder?: string
  orgId?: string
  allOption?: boolean
}

/**
 * Provides a stand-alone practitioner select widget
 * @param onChange Callback function to execute when a practitioner is selected
 *   arguments: (e, {name, value}) where value is the practitioner ID or "" if
 *   the ALL option is selected
 * @param name Name of the practitioner select widget in a form context
 * @param value Initial selected practitioner ID
 * @param orgId Optional org ID to constrain practitioners
 * @param placeholder Placeholder text. Default: Practitioners
 * @param allOption If true, include an 'ALL' option. Default: true
 */
function PractitionerSelect(props: PractitionerSelectProps): JSX.Element {
  const {
    onChange: onChangeProp,
    name: nameProp,
    value: valueProp,
    orgId: orgIdProp,
    placeholder: placeholderProp,
    allOption: isAllOptionProp
  } = props

  const [
    practitioners,
    setPractitioners
  ] = useState<Practitioner[]>([])
  const hasAllOption = isAllOptionProp !== false

  useEffect(() => {
    let mounted = true
    const orgId = orgIdProp || null
    const url = API_URL.PRACTITIONER_SEARCH('true')
    const query: Record<string, string> = { statusCode: 'ACTIVE' }
    if (orgId) {
      query.orgId = orgId
    }
    const params = searchGenerator(query, 5000, 0, null, undefined)
    api.get(url, { params }).then(({ data }: { data: Practitioner[] }) => {
      if (!mounted) return
      setPractitioners(data)
    })
    return () => { mounted = false }
  }, [])

  const handleChange = (
    event: React.SyntheticEvent,
    data: {
      value: string
    }
  ) => {
    if (onChangeProp) {
      const name = nameProp || 'practitionerSelect'
      const val = data.value === 'ALL'
        ? null
        : data.value
      onChangeProp(
        event,
        {
          name,
          value: val
        }
      )
    }
  }

  const baseOptions: DropdownOption[] = practitioners.map(practitioner => ({
    key: practitioner.id,
    value: practitioner.id,
    text: `${practitioner.lastName}, ${practitioner.firstName}`
  }))

  const options = hasAllOption === true
    ? [
      {
        key: 'ALL',
        text: 'All',
        value: 'ALL'
      },
      ...baseOptions
    ]
    : baseOptions

  const placeholder = placeholderProp || 'Practitioners'
  const value = valueProp || undefined

  return (
    <SafeResponsive
      as={ SafeDropdown }
      selection
      placeholder={ placeholder }
      onChange={ handleChange }
      options={ options }
      value={ value }
    />
  )
}

export default PractitionerSelect
